/**
 * Non-blocking health status banner.
 *
 * Replaces the old blocking `<AppLoading />` gate in OnboardedRoute.
 * Shows a compact top banner while backend is checking, starting, or down.
 *
 * Also surfaces a subtle "live updates off" indicator when the SSE
 * session-events stream is disabled or disconnected. SSE failure is not a
 * full outage — list queries still work via `refetchOnMount` /
 * `refetchOnReconnect` — but the user deserves to know that screen content
 * is no longer auto-updating from the server.
 *
 * Auto-hides when backend is healthy AND SSE is connected.
 */

import { useTranslation } from 'react-i18next';
import { RefreshCw, Wifi, WifiOff, Loader2, RadioTower } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSSEConnectionStatus } from '@/stores/sseConnection';

interface HealthStatusBannerProps {
  /** null = not yet checked, true = healthy, false = unhealthy */
  isBackendHealthy: boolean | null;
  /** Backend is loading models */
  isBackendStarting: boolean;
  /** Health check request in flight */
  isHealthChecking: boolean;
  /** Error message (null during cold-start grace period) */
  error: string | null;
  /** Trigger manual retry */
  onRetry: () => void;
}

export function HealthStatusBanner({
  isBackendHealthy,
  isBackendStarting,
  isHealthChecking,
  error,
  onRetry,
}: HealthStatusBannerProps) {
  const { t } = useTranslation();
  const sseStatus = useSSEConnectionStatus();

  const backendHealthy = isBackendHealthy === true && !isBackendStarting;
  // Only a *definitively* disabled stream is worth surfacing. A 'disconnected'
  // stream is mid-reconnect — a transient the hook recovers from on its own
  // (cold start, Cloud Run's ~300s stream cycling), with list queries
  // reconciling via refetch meanwhile — so surfacing it would just flash
  // non-actionable noise on every load and every ~5-minute cycle. 'connecting'
  // is likewise silent.
  const sseLiveUpdatesOff = sseStatus === 'disabled';

  // Hide entirely when backend is healthy AND SSE is delivering live updates.
  if (backendHealthy && !sseLiveUpdatesOff) return null;

  // Backend is fine but the live-event stream hit a definitive dead-end (an
  // out-of-date client vs a bumped server contract). This is the one SSE state
  // worth surfacing, and it carries the only action that fixes it: reloading
  // re-establishes the stream and picks up any new client build. Native iOS
  // PWA overscroll pull-to-refresh is suppressed in standalone mode, so this
  // banner is the only affordance the user has.
  if (backendHealthy && sseLiveUpdatesOff) {
    return (
      <button
        type="button"
        role="status"
        aria-live="polite"
        onClick={() => window.location.reload()}
        className={cn(
          'sticky top-0 z-50 px-4 py-1.5 text-xs font-medium w-full',
          'flex items-center justify-center gap-2',
          'animate-slide-down safe-area-top',
          'bg-neutral-100 text-neutral-700 border-b border-neutral-200',
          'hover:bg-neutral-200 active:bg-neutral-300 transition-colors cursor-pointer',
        )}
      >
        <RadioTower className="w-3.5 h-3.5 flex-shrink-0 opacity-60" />
        <span>
          {t('sse.liveUpdatesDisabled', {
            defaultValue: 'Live updates unavailable. Tap to reload.',
          })}
        </span>
      </button>
    );
  }

  const isDown = isBackendHealthy === false && !isBackendStarting;

  const getMessage = (): string => {
    if (isDown) return t('health.disconnected', 'Server unavailable');
    if (isBackendStarting) return t('health.starting', 'Server starting...');
    if (isHealthChecking) return t('health.checking', 'Checking connection...');
    return t('health.connecting', 'Connecting to server...');
  };

  const getIcon = () => {
    if (isHealthChecking && !isDown) return <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />;
    if (isDown) return <WifiOff className="w-3.5 h-3.5 flex-shrink-0" />;
    return <Wifi className="w-3.5 h-3.5 animate-pulse flex-shrink-0" />;
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'sticky top-0 z-50 px-4 py-2 text-xs font-medium',
        'flex items-center justify-center gap-2',
        'animate-slide-down',
        'safe-area-top',
        isDown
          ? 'bg-red-50 text-red-800 border-b border-red-200'
          : isBackendStarting
            ? 'bg-citation-50 text-citation-800 border-b border-citation-200'
            : 'bg-amber-50 text-amber-800 border-b border-amber-200',
      )}
    >
      {getIcon()}
      <span>{getMessage()}</span>
      {error && !isDown && (
        <span className="font-mono text-[10px] opacity-60">{error}</span>
      )}
      {isDown && !isHealthChecking && (
        <>
          <button
            type="button"
            onClick={onRetry}
            className={cn(
              'ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px]',
              'bg-red-100 hover:bg-red-200 transition-colors',
            )}
          >
            <RefreshCw className="w-3 h-3" />
            {t('actions.retry', 'Retry')}
          </button>
        </>
      )}
    </div>
  );
}
